import type { BalanceTransactionEntry, V1BillingStatement } from '@nadoutong/contracts';
import type { SheetCell, SheetSpec } from './types.ts';
import { moneyYuan } from './money.ts';

/** Chinese headers — always zh, per #178. */
export const STATEMENT_SHEET_HEADERS: readonly string[] = [
  '账期起',
  '账期止',
  '用量（元）',
  '期初应收（元）',
  '本期回款（元）',
  '期末应收（元）',
  '人民币参考期末应收（元）',
  '到期日',
  '状态',
];

export const TRANSACTION_SHEET_HEADERS: readonly string[] = [
  '时间',
  '类型',
  '金额（元）',
  '原因',
  '外部请求 ID',
];

export const STATEMENT_SHEET_NAME = '对账单';
export const TRANSACTION_SHEET_NAME = '余额流水';

const TX_TYPE_LABEL: Readonly<Record<string, string>> = {
  charge: '请求费用',
  gift_card_redeem: '礼品卡入账',
  gift_direct_issue: '赠送入账',
  gift_expiry_reclaim: '赠送到期回收',
  adjustment: '人工调整',
  topup: '充值入账',
  external_topup: '线下充值入账',
};

export function assembleStatementSheet(
  rows: readonly V1BillingStatement[],
  cnyPerUsd: number | null,
  todayYmd: string,
): SheetSpec {
  return {
    name: STATEMENT_SHEET_NAME,
    rows: [STATEMENT_SHEET_HEADERS, ...rows.map((row) => statementRow(row, cnyPerUsd, todayYmd))],
  };
}

export function assembleTransactionSheet(
  rows: readonly BalanceTransactionEntry[],
  cnyPerUsd: number | null,
): SheetSpec {
  return {
    name: TRANSACTION_SHEET_NAME,
    rows: [TRANSACTION_SHEET_HEADERS, ...rows.map((row) => transactionRow(row, cnyPerUsd))],
  };
}

function statementRow(
  row: V1BillingStatement,
  cnyPerUsd: number | null,
  todayYmd: string,
): readonly SheetCell[] {
  return [
    row.periodStart,
    row.periodEnd,
    moneyYuan(row.consumptionUsdMicros, row.consumptionUsdCents, cnyPerUsd),
    moneyYuan(row.openingArUsdMicros, row.openingArUsdCents, cnyPerUsd),
    moneyYuan(row.paymentsAppliedUsdMicros, row.paymentsAppliedUsdCents, cnyPerUsd),
    moneyYuan(row.closingArUsdMicros, row.closingArUsdCents, cnyPerUsd),
    row.cnyReferenceClosingArCents === null ? null : row.cnyReferenceClosingArCents / 100,
    row.dueDate,
    statementStatusLabel(row.status, row.dueDate, todayYmd),
  ];
}

function transactionRow(
  row: BalanceTransactionEntry,
  cnyPerUsd: number | null,
): readonly SheetCell[] {
  return [
    row.createdAt,
    TX_TYPE_LABEL[row.type] ?? row.type.replace(/_/g, ' '),
    moneyYuan(row.amountUsdMicros, row.amountUsdCents, cnyPerUsd),
    row.reason ?? '',
    row.externalRequestId ?? '',
  ];
}

export function statementStatusLabel(
  status: V1BillingStatement['status'],
  dueDate: string,
  todayYmd: string,
): string {
  if (status === 'closed') return '已结清';
  if (dueDate < todayYmd) return '逾期';
  return '未结清';
}
